import { bunja } from 'bunja';
import { atom } from 'jotai';
import { atomFamily } from 'jotai-family';
import { SetStateAction } from 'react';

import { getLocalStorageAtom } from '@flipster/react/misc/state/use-local-storage';
import { JotaiStoreScope } from '@flipster/utils/bunja-jotai';

import { authBunja } from '../shared/auth/state';
import { userRegulationBunja } from '../shared/regulation';
import { rawInstrumentsKeyBySymbolAtom } from '../shared/snapshot/state';

function getRecentSearchHistoryLocalStorageAtom(userId?: number) {
  return getLocalStorageAtom(`flipster.configs.v1.${userId}.recentSearchHistory`, []);
}

export const recentHistoryBunja = bunja(() => {
  const store = bunja.use(JotaiStoreScope);
  const { userAtom } = bunja.use(authBunja);
  const { isSpotOnlyRegulationAtom } = bunja.use(userRegulationBunja);
  const userIdAtom = atom((get) => get(userAtom)?.userId);

  const recentHistoryAtomFamily = atomFamily((symbol: string) =>
    atom((get) => {
      const instrument = get(rawInstrumentsKeyBySymbolAtom)?.[symbol];
      if (!instrument) return null;
      return {
        isSpot: instrument.isSpot,
        isListed: instrument.isListed,
        displayShortName: instrument.displayShortName,
      };
    }),
  );

  const historiesAtom = atom<string[]>((get) => {
    const userId = get(userIdAtom);
    const isSpotOnly = get(isSpotOnlyRegulationAtom);
    if (!userId) return [];

    const instrumentMap = get(rawInstrumentsKeyBySymbolAtom);
    const localStorageAtom = getRecentSearchHistoryLocalStorageAtom(userId);
    const histories = get(localStorageAtom).filter((symbol) => instrumentMap?.[symbol]?.isListed);
    if (isSpotOnly) return histories.filter((symbol) => instrumentMap?.[symbol]?.isSpot);
    return histories;
  });

  function setRecentSearchHistory(action: SetStateAction<string[]>): void {
    const userId = store.get(userIdAtom);
    if (!userId) return;
    const localStorageAtom = getRecentSearchHistoryLocalStorageAtom(userId);
    store.set(localStorageAtom, action);
  }

  function addRecentSearchHistory(value: string): void {
    const instrumentMap = store.get(rawInstrumentsKeyBySymbolAtom);
    setRecentSearchHistory((prev) => {
      return [...new Set([value, ...prev])].filter((symbol) => instrumentMap?.[symbol]?.isListed);
    });
  }

  function clearRecentSearchHistory(): void {
    setRecentSearchHistory([]);
  }

  return {
    historiesAtom,
    recentHistoryAtomFamily,
    addRecentSearchHistory,
    clearRecentSearchHistory,
  };
});
recentHistoryBunja.debugLabel = 'recentHistoryBunja';
